import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiFetch } from '../lib/api'
import type { TaskQueueItem, TaskQueueResponse } from '../lib/tasks'
import { patternFor, blocksRecord, type Pattern } from './patterns'
import AllClear from './AllClear'
import { useRkDecksEnabled } from './rkdecks/api'
import TaskPane from './TaskPane'
import {
  chaseIsRecommended,
  chaseKeyOf,
  chaseObjectLabelOf,
  chaseObligationLabelOf,
  isEvidenceChase,
} from './chaseGroups'
import Sentence from './Sentence'
import { taskSentence } from './taskSentence'

/** SENTENCES, NOT LABELS (Ale 2026-09-02): a queue row and the focus
 *  header read as ONE sentence carrying the task's record values —
 *  fund, object, figures, age — instead of title + type/fund/age labels.
 *  The engine title stays in the DOM (off-screen) for title-based flows. */
function TaskSentence({ task, testId }: { task: TaskQueueItem; testId: string }) {
  const s = taskSentence(task)
  return (
    <Sentence
      className="sn-lead"
      meaning={s.meaning}
      values={s.values}
      fallback={s.fallback}
      data-testid={testId}
    />
  )
}

/* Task focus — the WHOLE page is the current task (Ale 2026-06-10:
 * "all the task page shall be devoted to the current task with a small
 * bar to continue with next task"). The queue is a count + dropdown in
 * the bar, ordered blockers-first; J/K or the arrows move through it. */

interface WorkItem {
  id: string
  kind: 'task'
  pattern: Pattern
  title: string
  fundLabel: string
  blocks: boolean
  createdAt: string | null
  raw: TaskQueueItem
  /** Evidence-chase queue semantics (D-3a): chases group one-row-per-object
   *  in the queue; recommended (non-blocking) chases sit behind a collapsed
   *  toggle. */
  chaseKey: string | null
  chaseObjectLabel: string | null
  recommended: boolean
}

function age(iso: string | null): string {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  const h = Math.floor(ms / 3_600_000)
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60_000))}m`
  if (h < 48) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

type QueueRow =
  | { kind: 'single'; item: WorkItem }
  | { kind: 'group'; key: string; label: string; items: WorkItem[] }

/** One row per chase object (order-preserving); 1-item groups collapse back
 *  to plain rows; non-chase items pass through untouched. */
function buildQueueRows(list: WorkItem[]): QueueRow[] {
  const rows: QueueRow[] = []
  const groups = new Map<string, { kind: 'group'; key: string; label: string; items: WorkItem[] }>()
  for (const i of list) {
    if (i.chaseKey) {
      let g = groups.get(i.chaseKey)
      if (!g) {
        g = { kind: 'group', key: i.chaseKey, label: i.chaseObjectLabel ?? i.fundLabel, items: [] }
        groups.set(i.chaseKey, g)
        rows.push(g)
      }
      g.items.push(i)
    } else {
      rows.push({ kind: 'single', item: i })
    }
  }
  return rows.map((r) =>
    r.kind === 'group' && r.items.length === 1 ? { kind: 'single', item: r.items[0] } : r
  )
}

export default function TasksWorkbench() {
  const [items, setItems] = useState<WorkItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [queueOpen, setQueueOpen] = useState(false)
  // Deep link: /v2/tasks?task=<task_id> preselects that task once the queue
  // loads (the queue had no addressable URL before — everything else
  // dead-ended on the unsorted list).
  const [searchParams] = useSearchParams()
  const deepLinkTaskId = searchParams.get('task')
  // D-3a: per-object chase groups start collapsed; the recommended
  // (non-blocking) section starts closed.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  // RK W4: recon tasks fold into deck cards while the rk_decks flag is on.
  const rkDecksOn = useRkDecksEnabled()
  const [recommendedOpen, setRecommendedOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const tasksRes = await apiFetch<TaskQueueResponse>('/api/tasks?status=open')
        if (cancelled) return
        const taskItems: WorkItem[] = (tasksRes.items ?? []).map((t) => ({
          id: `task:${t.task_id}`,
          kind: 'task',
          pattern: patternFor(t.task_type),
          title: t.title,
          fundLabel: t.fund_name || 'fund not yet identified',
          blocks: blocksRecord(t.task_type, t.priority),
          createdAt: t.created_at,
          raw: t,
          chaseKey: chaseKeyOf(t),
          chaseObjectLabel: chaseObjectLabelOf(t),
          recommended: isEvidenceChase(t) && chaseIsRecommended(t),
        }))
        // Per-chain proposal_review tasks ARE the review surface now (the #617
        // fold). The old standalone proposal_group fallback was removed: it
        // double-surfaced every group (its dedup compared a chain target_id
        // against a group id, never matched) and dead-ended on already-emptied
        // groups (officer-sim #5/#7/#9). The queue is tasks only — its count now
        // matches Home's.
        // Blockers first (unchanged), recommended (non-blocking) chases last
        // (D-3a) — the J/K walk reaches them only after the real work.
        const all = [...taskItems].sort(
          (a, b) =>
            Number(b.blocks) - Number(a.blocks) || Number(a.recommended) - Number(b.recommended)
        )
        setItems(all)
        const deepLinked = deepLinkTaskId
          ? all.find((i) => i.id === `task:${deepLinkTaskId}`)
          : undefined
        setSelectedId((cur) => cur ?? deepLinked?.id ?? all[0]?.id ?? null)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load tasks')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once; the deep link only seeds the initial selection
  }, [])

  const idx = useMemo(() => items.findIndex((i) => i.id === selectedId), [items, selectedId])
  const selected = idx >= 0 ? items[idx] : null

  const move = useCallback(
    (delta: number) => {
      if (items.length === 0) return
      const next = items[Math.min(Math.max(idx + delta, 0), items.length - 1)]
      if (next) setSelectedId(next.id)
    },
    [items, idx]
  )

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'j') move(1)
      if (e.key === 'k') move(-1)
      if (e.key === 'Escape') setQueueOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [move])

  const blockers = items.filter((i) => i.blocks)
  const routine = items.filter((i) => !i.blocks && !i.recommended)
  const recommended = items.filter((i) => !i.blocks && i.recommended)

  const selectItem = (id: string) => {
    setSelectedId(id)
    setQueueOpen(false)
  }

  const renderQueueRows = (list: WorkItem[]) =>
    buildQueueRows(list).map((row) => {
      if (row.kind === 'single') {
        const i = row.item
        return (
          <div
            key={i.id}
            className={`qitem${i.id === selectedId ? ' sel' : ''}`}
            onClick={() => selectItem(i.id)}
          >
            <div className="qt" title={i.title}>
              <TaskSentence task={i.raw} testId="queue-row-sentence" />
              <span className="sr-title">{i.title}</span>
            </div>
          </div>
        )
      }
      const open = expandedGroups.has(row.key)
      const anySelected = row.items.some((i) => i.id === selectedId)
      return (
        <div key={`group:${row.key}`} data-testid="chase-group">
          <div
            className={`qitem${anySelected && !open ? ' sel' : ''}`}
            onClick={() =>
              setExpandedGroups((prev) => {
                const next = new Set(prev)
                if (next.has(row.key)) next.delete(row.key)
                else next.add(row.key)
                return next
              })
            }
          >
            <div className="qt" data-testid="chase-group-title">
              {open ? '▾' : '▸'}{' '}
              <Sentence
                meaning="chase.group"
                values={{ count: row.items.length, object: row.label }}
                fallback={`${row.items.length} evidence chases — ${row.label}`}
              />
            </div>
            <div className="qm">{row.items[0].fundLabel}</div>
          </div>
          {open &&
            row.items.map((i) => (
              <div
                key={i.id}
                data-testid="chase-group-item"
                className={`qitem${i.id === selectedId ? ' sel' : ''}`}
                style={{ paddingLeft: 26 }}
                onClick={() => selectItem(i.id)}
              >
                {/* Obligation leads; the full title stays in the row (and in
                    the DOM text) so title-based flows keep matching. */}
                <div className="qt" title={i.title}>
                  <b>{chaseObligationLabelOf(i.raw)}</b>
                  <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
                    {i.title}
                  </span>
                </div>
                <div className="qm">
                  {i.createdAt ? <span className="mono">{age(i.createdAt)}</span> : null}
                </div>
              </div>
            ))}
        </div>
      )
    })

  if (loading)
    return (
      <main className="focus">
        <div className="dbody">
          <div className="skeleton-zone" style={{ flex: 1 }}>
            loading…
          </div>
        </div>
      </main>
    )

  if (error || !selected)
    return (
      <main className="focus">
        <div className="dbody">
          <div className="skeleton-zone" style={{ flex: 1 }}>
            {/* The affirmative close moment (value proposal #3): an empty
                queue after a drop is a WIN — say what was processed. */}
            {error ?? <AllClear />}
          </div>
        </div>
      </main>
    )

  return (
    <main className="focus">
      <div className="fhead">
        <button className="vbtn sm qbtn" onClick={() => setQueueOpen((o) => !o)}>
          {items.length} open <span style={{ color: 'var(--faint)' }}>▾</span>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ft" title={selected.title}>
            <TaskSentence task={selected.raw as TaskQueueItem} testId="task-header-sentence" />
            <span className="sr-title">{selected.title}</span>
          </div>
        </div>
        <span className="mono" style={{ fontSize: 'var(--text-12)', color: 'var(--faint)' }}>
          {idx + 1} / {items.length}
        </span>
        <button className="vbtn sm" onClick={() => move(-1)} disabled={idx === 0}>
          ↑
        </button>
        <button className="vbtn sm" onClick={() => move(1)} disabled={idx === items.length - 1}>
          ↓
        </button>
      </div>

      {queueOpen && (
        <div className="qdrop">
          {blockers.length > 0 && <div className="qsec">Blocks the record · {blockers.length}</div>}
          {renderQueueRows(blockers)}
          {routine.length > 0 && <div className="qsec">Routine · {routine.length}</div>}
          {renderQueueRows(routine)}
          {/* D-3a: recommended (non-blocking) chases behind a collapsed
              toggle, defaulted closed — they never block the record and
              must not crowd the real queue. */}
          {recommended.length > 0 && (
            <div
              className="qsec"
              data-testid="recommended-toggle"
              style={{ cursor: 'pointer' }}
              onClick={() => setRecommendedOpen((o) => !o)}
            >
              {recommendedOpen ? '▾' : '▸'} Recommended — non-blocking · {recommended.length}
            </div>
          )}
          {recommendedOpen && renderQueueRows(recommended)}
        </div>
      )}

      <div className="dbody" onClick={() => queueOpen && setQueueOpen(false)}>
        <TaskPane
          key={selected.id}
          task={selected.raw as TaskQueueItem}
          rkDecksOn={rkDecksOn}
          onResolved={() => {
            setItems((prev) => {
              const next = prev.filter((i) => i.id !== selected.id)
              const at = Math.min(idx, next.length - 1)
              setSelectedId(next[at]?.id ?? null)
              return next
            })
          }}
        />
      </div>

      <div className="dfoot">
        <button className="vbtn" onClick={() => move(1)} disabled={idx === items.length - 1}>
          Skip → next
        </button>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 'var(--text-12)', color: 'var(--faint)' }}>
          <span className="kbd">J</span> <span className="kbd">K</span> previous / next · every
          action lands in the audit trail
        </span>
      </div>
    </main>
  )
}
